/**
 * actLegibility (S136 / BL-121 / PT-38 #5b) — the single source for the
 * persistent, glanceable "which act am I in?" answer.
 *
 * The S130 act-door cards (`ActCard`) name the act at the threshold and then
 * fade; the S126 epoch tint escalates but reads as a gradient, not three doors.
 * So a screenshot from the middle of an act couldn't answer which act it is.
 * This maps a wave to its act identity — a number, an i18n label key (EN+DE) and
 * a per-act glance COLOUR stepped from the central palette (clean phosphor green
 * → her signal magenta → her dominion violet) — consumed by the persistent HUD
 * act badge so any-wave the act reads at a glance.
 *
 * Pure: no DOM, no sim, no state. Colours are central tokens only (no new hex);
 * the magenta/violet match the act-card accents (ECHELON, D-224) so the door and
 * the persistent marker tell one story.
 */
import { GAME } from "../core/config.ts";
import { COLORS, ECHELON } from "../rendering/designTokens.ts";

export interface ActIdentity {
  /** 1 | 2 | 3. Pre-game / wave ≤ 0 clamps to Act I; endless (> final) stays III. */
  act: 1 | 2 | 3;
  /** i18n key for the persistent badge label (EN+DE; e.g. "ACT II" — D-335
   *  dropped the subtitle from this short-form label). */
  key: "hud.act.1" | "hud.act.2" | "hud.act.3";
  /** Per-act glance colour — central tokens, matching the act-card accents. */
  color: string;
}

const ACT_I: ActIdentity = {
  act: 1,
  key: "hud.act.1",
  color: COLORS.SENTINEL_GREEN, // the clean board you protect
};
const ACT_II: ActIdentity = {
  act: 2,
  key: "hud.act.2",
  color: ECHELON.SIGNAL, // her signal arrives (magenta, D-224)
};
const ACT_III: ActIdentity = {
  act: 3,
  key: "hud.act.3",
  color: ECHELON.DEEP, // dominion (her depth violet)
};

/** The act identity for a wave (the persistent legibility source). */
export function actForWave(wave: number): ActIdentity {
  if (wave <= GAME.WAVES_ACT_I) return ACT_I;
  if (wave <= GAME.WAVES_ACT_II) return ACT_II;
  return ACT_III;
}
